/**
 * Admin Add-on Orders / Fulfillment Queue (Phase 1: Add-ons)
 *
 * Paginated list of add-on purchases with status / family / fulfillment filters.
 * Admins can set a quote price, mark orders fulfilled, or cancel them.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Loader2,
  ArrowLeft,
  Filter,
  X,
  CheckCircle2,
  XCircle,
  Tag,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import adminService, {
  type AddonPurchase,
  type AddonPurchaseStatus,
  type AddonFamily,
  type AddonFulfillment,
} from '../../../../services/adminService';

const FAMILIES: AddonFamily[] = [
  'VISIBILITY_LIFECYCLE',
  'LISTING_ENRICHMENT',
  'AI_STUDIO',
  'TRUST_VERIFICATION',
  'PROFESSIONAL_CONTENT',
  'PERSONAL',
  'OTHER',
];

const STATUSES: AddonPurchaseStatus[] = [
  'PENDING_PAYMENT',
  'PENDING_FULFILLMENT',
  'ACTIVE',
  'FULFILLED',
  'EXPIRED',
  'CANCELLED',
  'REFUNDED',
];

const FULFILLMENTS: AddonFulfillment[] = ['AUTO', 'MANUAL_ORDER'];

const STATUS_COLORS: Record<AddonPurchaseStatus, string> = {
  PENDING_PAYMENT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  PENDING_FULFILLMENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  ACTIVE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  FULFILLED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  EXPIRED: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
  CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  REFUNDED: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

const PAGE_SIZE = 20;

// ============================================================================
// SET PRICE MODAL
// ============================================================================

function SetPriceModal({
  purchase,
  onSave,
  onClose,
  isLoading,
}: {
  purchase: AddonPurchase;
  onSave: (priceSar: number, orderNotes?: string) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { t, i18n } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!price.trim()) return;
    onSave(Number(price), notes.trim() || undefined);
  };

  const inputClass =
    'w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-bold text-[var(--pv-color-primary)] dark:text-white">
            {t('admin.setPriceTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <p className="font-medium text-gray-900 dark:text-white">
              {purchase.addon?.name ?? '-'}
            </p>
            <p className="text-sm text-gray-500">{purchase.user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.setPriceLabel')}</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className={inputClass}
              min="0"
              step="0.01"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.orderNotes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`${inputClass} resize-none`}
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={isLoading || !price.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('admin.saving')}
                </>
              ) : (
                t('admin.save')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminAddonOrdersPage() {
  const { t, i18n } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';

  const [purchases, setPurchases] = useState<AddonPurchase[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [familyFilter, setFamilyFilter] = useState<string>('');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<string>('');
  const [pricingPurchase, setPricingPurchase] = useState<AddonPurchase | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchases = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminService.getAddonPurchases({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        family: familyFilter || undefined,
        fulfillment: fulfillmentFilter || undefined,
      });
      setPurchases(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      console.error('Failed to fetch add-on orders:', err);
      setError(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, familyFilter, fulfillmentFilter, t]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, familyFilter, fulfillmentFilter]);

  const runUpdate = async (
    id: string,
    params: { priceSar?: number; status?: AddonPurchaseStatus; orderNotes?: string },
  ) => {
    try {
      setIsSaving(true);
      setError(null);
      await adminService.updateAddonOrder(id, params);
      setPricingPurchase(null);
      fetchPurchases();
    } catch (err: unknown) {
      console.error('Failed to update add-on order:', err);
      setError(err instanceof Error ? err.message : t('admin.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetPrice = (priceSar: number, orderNotes?: string) => {
    if (!pricingPurchase) return;
    runUpdate(pricingPurchase.id, { priceSar, orderNotes });
  };

  const handleMarkFulfilled = (purchase: AddonPurchase) => {
    runUpdate(purchase.id, { status: 'FULFILLED' });
  };

  const handleCancel = (purchase: AddonPurchase) => {
    runUpdate(purchase.id, { status: 'CANCELLED' });
  };

  const formatPrice = (purchase: AddonPurchase) => {
    if (purchase.addon?.isQuoteBased && purchase.priceSar === 0) {
      return t('quoteBased');
    }
    return t('priceSar', { price: purchase.priceSar.toLocaleString() });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                {t('admin.ordersTitle')}
              </h1>
              <p className="text-gray-500">{t('admin.ordersSubtitle')}</p>
            </div>
          </div>
          <Link to="/admin/subscriptions/addons">
            <Button variant="outline">{t('admin.catalogTitle')}</Button>
          </Link>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="">{t('admin.allStatuses')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`status.${s}`)}
              </option>
            ))}
          </select>
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="">{t('admin.allFamilies')}</option>
            {FAMILIES.map((fam) => (
              <option key={fam} value={fam}>
                {t(`family.${fam}`)}
              </option>
            ))}
          </select>
          <select
            value={fulfillmentFilter}
            onChange={(e) => setFulfillmentFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="">{t('admin.allFulfillment')}</option>
            {FULFILLMENTS.map((f) => (
              <option key={f} value={f}>
                {t(`fulfillment.${f}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colBuyer')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colAddon')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colFamily')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colStatus')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colPrice')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colCreated')}
                  </th>
                  <th className="px-4 py-3 text-end text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {purchases.map((purchase) => {
                  const isQuotePending =
                    purchase.status === 'PENDING_FULFILLMENT' &&
                    purchase.addon?.isQuoteBased;
                  const canFulfill =
                    purchase.status === 'PENDING_FULFILLMENT' ||
                    purchase.status === 'ACTIVE';
                  const canCancel =
                    purchase.status === 'PENDING_PAYMENT' ||
                    purchase.status === 'PENDING_FULFILLMENT';

                  return (
                    <tr
                      key={purchase.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {purchase.user?.firstName} {purchase.user?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{purchase.user?.email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {purchase.addon?.name ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {purchase.addon?.family ? t(`family.${purchase.addon.family}`) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[purchase.status]}`}
                        >
                          {t(`status.${purchase.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {formatPrice(purchase)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {format(new Date(purchase.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="px-4 py-3 text-end whitespace-nowrap">
                        {isQuotePending && (
                          <button
                            onClick={() => setPricingPurchase(purchase)}
                            className="p-2 hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/30 text-[#004D40] rounded-lg"
                            title={t('setPrice')}
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                        )}
                        {canFulfill && (
                          <button
                            onClick={() => handleMarkFulfilled(purchase)}
                            disabled={isSaving}
                            className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 rounded-lg disabled:opacity-50"
                            title={t('admin.markFulfilled')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        {canCancel && (
                          <button
                            onClick={() => handleCancel(purchase)}
                            disabled={isSaving}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg disabled:opacity-50"
                            title={t('admin.cancelOrder')}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {purchases.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {t('admin.emptyOrders')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {page} / {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 rounded-lg border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Set Price Modal */}
      {pricingPurchase && (
        <SetPriceModal
          purchase={pricingPurchase}
          onSave={handleSetPrice}
          onClose={() => setPricingPurchase(null)}
          isLoading={isSaving}
        />
      )}
    </div>
  );
}
